/* ============================================================
   cart-kaching-sync.js — keeps Kaching quantity-break bundles in
   sync when the customer changes qty in the cart drawer / cart page.

   Zero configuration: tier structure (bids, gift ids, gift qty,
   gift variant) is read at runtime from the Kaching JSON blocks on
   the product page of the line item itself, cached in sessionStorage.
   Only lines that already carry a __kaching_bundles property are
   ever touched — all other products are invisible to this script.

   The theme renders bundle rows with data-vg-kb-main / data-vg-kb-gift
   tds (see cart-products.liquid); those rows have NO Horizon on:click
   bindings, so this script is their only controller (no event races).

   Toggles come from window.VG_KBSYNC (emitted in header-actions.liquid
   from theme settings).
   ============================================================ */
(() => {
  'use strict';
  const CFG = window.VG_KBSYNC || {};

  /* ---------- cart upsell ("You may also like") ----------
     Registered BEFORE the enabled gate so the upsell works even with the
     bundle sync toggled off (helpers below are hoisted declarations).
     Add = plain paid line (no Kaching properties), then the usual settled
     re-render — same flicker-free path as every other drawer control. */
  /* the drawer auto-focuses its close button on open — after a tap/click that
     focus ring reads as a stray circle. Blur it for pointer-driven focus only;
     keyboard TAB (no recent pointerdown) keeps the ring for accessibility. */
  let vgLastPointer = 0;
  document.addEventListener('pointerdown', () => { vgLastPointer = Date.now(); }, true);
  document.addEventListener('focusin', e => {
    const t = e.target;
    if (t && t.classList && t.classList.contains('cart-drawer__close-button') && Date.now() - vgLastPointer < 1200) t.blur();
  }, true);

  const upsellBusy = new Set();
  document.addEventListener('click', async e => {
    const nav = e.target.closest('[data-vg-upsell-nav]');
    if (nav) {
      e.preventDefault();
      const row = nav.closest('.vg-upsell')?.querySelector('[data-vg-upsell-row]');
      const cards = row ? row.querySelectorAll('.vg-upsell__card') : [];
      if (cards.length > 1) {
        // index-based: rapid clicks step from the TARGET card, not the
        // mid-flight scroll position, so they can never land between snaps
        const step = cards[0].getBoundingClientRect().width + 12;
        const fresh = row._vgNavT && Date.now() - row._vgNavT < 700 && row.dataset.vgIdx != null;
        const cur = fresh ? +row.dataset.vgIdx : Math.round(row.scrollLeft / step);
        const next = Math.max(0, Math.min(cards.length - 1, cur + (+nav.dataset.vgUpsellNav || 1)));
        row.dataset.vgIdx = next;
        row._vgNavT = Date.now();
        row.scrollTo({ left: next * step, behavior: 'smooth' });
      }
      return;
    }
    const btn = e.target.closest('[data-vg-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const vid = +btn.dataset.vgUpsellAdd;
    if (!vid || upsellBusy.has(vid)) return;
    upsellBusy.add(vid);
    btn.classList.add('vg-upsell-busy');
    const sectionId = sectionIdFor(btn);
    try {
      await postJSON('/cart/add.js', { items: [{ id: vid, quantity: 1 }] });
      // success: morph the button into a checkmark before the re-render
      btn.classList.remove('vg-upsell-busy');
      btn.style.width = btn.offsetWidth + 'px';   // keep the pill's width while the label swaps
      btn.classList.add('vg-upsell-added');
      btn.innerHTML = '<svg class="vg-upsell__check-pop" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      await new Promise(r => setTimeout(r, 750));
      const box = btn.closest('.vg-upsell');
      if (box && box.classList.contains('vg-upsell--single')) {
        // the only offered product is now in the cart — collapse the section
        // and keep it hidden across re-renders for the rest of this page view
        window._vgUpsellHide = true;
        const h = box.offsetHeight;
        box.style.overflow = 'hidden';
        try {
          await box.animate(
            [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 320, easing: 'ease' }
          ).finished;
        } catch (e) { /* animation interrupted — hide anyway */ }
        box.style.display = 'none';
      }
      await refreshSection(sectionId).catch(() => {});
    } catch (err) {
      await refreshSection(sectionId).catch(() => {});
      console.warn('[vg-upsell]', err);
    } finally {
      upsellBusy.delete(vid);
      const b = document.querySelector('[data-vg-upsell-add="' + vid + '"]');
      if (b) b.classList.remove('vg-upsell-busy');
    }
  }, true);

  if (!CFG.enabled) return;

  const TTL_MS = 15 * 60 * 1000;
  const inFlight = new Set();

  /* ---------- smooth updates ----------
     Horizon's morph REPLACES the <dialog> on every cart change, so the
     open animation replays (flicker). Timing-based suppression is fragile
     (slow ops re-render late), so instead we track real drawer state:
     while `html.vg-drawer-open` is set, dialog animations are disabled.
     - set ~250ms AFTER a genuine open (lets the real slide-in play once)
     - cleared ~400ms after a genuine close (morphs close+reopen within
       ~100ms, so the class persists straight through them)               */
  if (CFG.smooth) {
    const h = document.documentElement;
    const isOpen = () => !!document.querySelector('.cart-drawer__dialog[open]');
    const sync = () => {
      clearTimeout(h._vgOpenT);
      clearTimeout(h._vgCloseT);
      if (isOpen()) {
        if (!h.classList.contains('vg-drawer-open')) {
          h._vgOpenT = setTimeout(() => { if (isOpen()) h.classList.add('vg-drawer-open'); }, 250);
        }
      } else {
        h._vgCloseT = setTimeout(() => { if (!isOpen()) h.classList.remove('vg-drawer-open'); }, 400);
      }
    };
    new MutationObserver(sync).observe(document.body, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['open']
    });
    sync();
  }

  /* ---------- runtime config: parse a product page's Kaching JSON.
     Works on a fetched document OR the live PDP document itself. ---------- */
  function parseKachingConfigFromDoc(doc, bid) {
    let bars = null;
    let dealMeta = null;
    const products = {};
    const varProd = {};
    let currency = null;
    for (const s of doc.querySelectorAll('script[type="application/json"]')) {
      let j;
      try { j = JSON.parse(s.textContent); } catch (e) { continue; }
      if (!j || typeof j !== 'object') continue;
      if (Array.isArray(j.dealBars)) {
        // prefer the block whose bars include the line's current bid
        if (!bars || j.dealBars.some(b => b.id === bid)) {
          bars = j.dealBars;
          // deal identity for self-built cart payloads: nanoId = deal short id,
          // versionId = the `v` uuid (rotates on every dashboard edit)
          dealMeta = { deal: j.nanoId || null, v: j.versionId || null };
        }
      } else if (j.id && j.handle && Array.isArray(j.variants)) {
        products['gid://shopify/Product/' + j.id] = j;
        for (const vv of (j.variants || [])) varProd[vv.id] = j.id;
      } else if (j.shopifyDomain) {
        currency = j.marketCurrencyCode || null;
      }
    }
    if (!bars) throw new Error('no dealBars');

    const parsed = bars
      .filter(b => b.dealBarType === 'quantity-break')
      .map(b => {
        if ((b.freeGifts || []).length > 1) console.warn('[vg-kb-sync] tier', b.id, 'has multiple gifts — only the first is synced');
        const g = (b.freeGifts || [])[0] || null;
        return {
          qty: b.quantity,
          bid: b.id,
          giftId: g ? g.id : null,
          giftQty: g ? g.quantity : 0,
          giftVariantId: g && products[g.productGID] ? products[g.productGID].selectedVariantId : null,
          giftOnlySubs: !!(g && g.applyOnlyForSubscriptions),
          // a bar can pin its own selling plan (per-tier subscription setups)
          spGid: b.sellingPlanGid ? parseInt(String(b.sellingPlanGid).split('/').pop(), 10) || null : null,
          spEnabled: !!b.sellingPlanEnabled,
          discType: b.discountType,
          discValue: b.discountValue
        };
      })
      .sort((a, b) => a.qty - b.qty);
    if (!parsed.length) throw new Error('no quantity-break bars');

    /* selling plans per variant, sorted by delivery interval (parsed from the
       plan name, e.g. "Lieferung alle 4 Wochen") — used for the subscription
       upsell and the qty→frequency mapping (1→shortest, 2→middle, 3+→longest) */
    const planData = {};
    for (const p of Object.values(products)) {
      const names = {};
      (p.sellingPlans || []).forEach(sp => { names[sp.id] = sp.name; });
      for (const v of (p.variants || [])) {
        const plans = (v.sellingPlans || []).map(vp => ({
          id: vp.id,
          name: names[vp.id] || '',
          weeks: parseInt((names[vp.id] || '').replace(/\D+/g, ' ').trim().split(' ')[0], 10) || 0,
          unit: vp.price,
          base: v.price,
          // live plan discount, derived from prices — any adjustment type
          pct: v.price > 0 ? Math.round((1 - vp.price / v.price) * 100) : 0
        })).sort((a, b) => (a.weeks - b.weeks) || (a.id - b.id));
        if (plans.length) planData[v.id] = plans;
      }
    }

    return {
      t: Date.now(),
      bars: parsed,
      planData,
      currency: currency,
      deal: dealMeta ? dealMeta.deal : null,
      dealV: dealMeta ? dealMeta.v : null,
      varProd: varProd,
      // tier quantities in the deal block's DOM order (for selected-bar → qty mapping on the PDP)
      barQtysDomOrder: bars.filter(b => b.dealBarType === 'quantity-break').map(b => b.quantity)
    };
  }

  async function getDealConfig(productUrl, bid) {
    const key = 'vgkb7:' + productUrl;   // v7: + deal nanoId/versionId + variant→product map
    try {
      const c = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (c && Date.now() - c.t < TTL_MS && c.bars.some(b => b.bid === bid)) return c;
    } catch (e) { /* fall through to fetch */ }
    const res = await fetch(productUrl, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error('config fetch ' + res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const out = parseKachingConfigFromDoc(doc, bid);
    try { sessionStorage.setItem(key, JSON.stringify(out)); } catch (e) { /* quota — ignore */ }
    return out;
  }

  /* qty 4+ maps to the top tier (owner decision); gaps map down */
  function pickTier(bars, qty) {
    let best = bars[0];
    for (const b of bars) if (b.qty <= qty) best = b;
    return best;
  }

  /* delivery frequency follows quantity: 1 → shortest plan, 2 → middle, 3+ → longest */
  function mapPlanForQty(cfg, variantId, qty) {
    const plans = cfg.planData && cfg.planData[variantId];
    if (!plans || !plans.length) return null;
    return plans[Math.min(qty - 1, plans.length - 1)];
  }

  /* Kaching prices, computed from scratch (independent of the cart's current
     subscription state). Kaching's round-UP to whole units (price_rounding_v2
     ".00") only happens when ITS tier discount fires — a no-discount tier
     (qty 1) keeps the untouched unit price. Verified against live cart maths. */
  function kachingTotal(unitPrice, cfg, qty) {
    const tier = pickTier(cfg.bars, qty);
    let tot = unitPrice * qty;
    if (tier && tier.discType === 'percentage' && tier.discValue) {
      tot *= 1 - tier.discValue / 100;
      tot = Math.ceil(tot / 100) * 100;
    }
    return Math.round(tot);
  }

  function estimateSubTotal(cfg, variantId, qty) {
    const plan = mapPlanForQty(cfg, variantId, qty);
    if (!plan) return null;
    return kachingTotal(plan.unit, cfg, qty);
  }

  function estimateOnceTotal(cfg, variantId, qty) {
    const plans = cfg.planData && cfg.planData[variantId];
    if (!plans || !plans.length || !plans[0].base) return null;
    return kachingTotal(plans[0].base, cfg, qty);
  }

  function formatMoney(cents, cur) {
    const sym = { EUR: '€', USD: '$', GBP: '£' }[cur] || (cur + ' ');
    return sym + Math.round(cents / 100);   // whole units only — owner: no decimals in the buttons
  }

  /* warm the cache as soon as a bundle row exists so the first
     +/- press never waits on the product-page fetch */
  function warm() {
    const el = document.querySelector('td[data-vg-kb-main]');
    if (!el) return;
    try {
      const props = parseKb(el.dataset.props);
      if (props) getDealConfig(el.dataset.url, props.bid).catch(() => {});
    } catch (e) { /* ignore */ }
  }
  document.addEventListener('DOMContentLoaded', () => { warm(); updateSubUpsell(); });
  document.addEventListener('cart:update', () => setTimeout(() => { warm(); updateSubUpsell(); }, 350), true);
  warm();
  setTimeout(() => updateSubUpsell(), 600);

  /* ---------- helpers ---------- */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok) return r.json().catch(() => ({})).then(j => {
        throw new Error(j.description || j.message || url + ' ' + r.status);
      });
      return r.json();
    });
  }

  /* Liquid HTML-escapes property values (&quot;) — decode before JSON.parse.
     Decoding a clean string is a no-op, so this is safe either way. */
  function unent(s) {
    if (!s || s.indexOf('&') === -1) return s;
    const t = document.createElement('textarea');
    t.innerHTML = s;
    return t.value;
  }

  function parseKb(raw) {
    try { return JSON.parse(unent(raw)); } catch (e) { return null; }
  }

  /* item.properties may serialize as {name: value} or as [[name, value], …] */
  function normProps(raw) {
    let p;
    try { p = JSON.parse(raw || '{}'); } catch (e) { return {}; }
    if (Array.isArray(p)) p = Object.fromEntries(p);
    if (!p || typeof p !== 'object') return {};
    for (const k of Object.keys(p)) if (typeof p[k] === 'string') p[k] = unent(p[k]);
    return p;
  }

  function groupIdOf(td) {
    const p = parseKb(td.dataset.props);
    return p ? p.id : null;
  }

  function giftTdsForGroup(group) {
    return [...document.querySelectorAll('td[data-vg-kb-gift]')]
      .filter(td => groupIdOf(td) === group);
  }

  function sectionIdFor(el) {
    const sec = el.closest('[id^="shopify-section-"]');
    return sec ? sec.id.replace('shopify-section-', '') : null;
  }

  function setBusy(mainTd, group, busy) {
    const rows = [mainTd.closest('tr'), ...giftTdsForGroup(group).map(td => td.closest('tr'))];
    rows.forEach(r => r && r.classList.toggle('vg-kb-busy', busy));
  }

  /* ---------- render: transplant fresh section HTML below the dialog
     node so the drawer never closes/reopens ---------- */
  function renderSection(sectionId, html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const liveSection = document.getElementById('shopify-section-' + sectionId);
    if (!liveSection) return false;

    const liveInner = liveSection.querySelector('.cart-drawer__inner');
    const newInner = doc.querySelector('.cart-drawer__inner');
    if (liveInner && newInner) {
      liveInner.replaceChildren(...newInner.childNodes);
      // refresh the header cart-count bubble (lives outside the dialog)
      const liveBubbleHost = liveSection.querySelector('[data-testid="cart-drawer-trigger"]');
      const newBubbleHost = doc.querySelector('[data-testid="cart-drawer-trigger"]');
      if (liveBubbleHost && newBubbleHost) liveBubbleHost.innerHTML = newBubbleHost.innerHTML;
      keepUpsellHidden();
      return true;
    }
    // /cart page (no dialog to preserve): swap the whole section content
    const newSection = doc.getElementById('shopify-section-' + sectionId) || doc.body;
    liveSection.replaceChildren(...newSection.childNodes);
    keepUpsellHidden();
    return true;
  }

  /* a just-added single-product upsell stays collapsed across re-renders
     for the rest of this page view (server keeps rendering it) */
  function keepUpsellHidden() {
    if (!window._vgUpsellHide) return;
    const u = document.querySelector('.vg-upsell--single');
    if (u) u.style.display = 'none';
  }

  /* keep the subscription upsell in step after every transplant */
  const _renderSection = renderSection;
  renderSection = function (sectionId, html) {
    const ok = _renderSection(sectionId, html);
    if (ok) { try { updateSubUpsell(); } catch (e) { /* ignore */ } }
    return ok;
  };

  /* ---------- the swap ---------- */
  async function swap(mainTd, targetQty) {
    const props = parseKb(mainTd.dataset.props);          // {deal, main, id, bid}
    if (!props) return;
    const group = props.id;
    if (inFlight.has(group)) return;
    inFlight.add(group);
    setBusy(mainTd, group, true);

    const giftTds = giftTdsForGroup(group);
    const sectionId = sectionIdFor(mainTd) ||
      sectionIdFor(document.querySelector('.cart-drawer__dialog') || document.body);

    // capture originals for recovery
    const originals = [{
      id: +mainTd.dataset.variant,
      quantity: +mainTd.dataset.qty,
      properties: normProps(mainTd.dataset.allProps),
      sp: mainTd.dataset.sellingPlan ? +mainTd.dataset.sellingPlan : null
    }, ...giftTds.map(td => ({
      id: +td.dataset.variant,
      quantity: +td.dataset.qty,
      properties: normProps(td.dataset.allProps),
      sp: null
    }))];

    let mutated = false;   // set once the cart has actually been touched
    try {
      const updates = { [mainTd.dataset.key]: 0 };
      giftTds.forEach(td => { updates[td.dataset.key] = 0; });

      const items = [];
      if (targetQty > 0) {
        const cfg = await getDealConfig(mainTd.dataset.url, props.bid);
        const tier = pickTier(cfg.bars, targetQty);

        const mainProps = normProps(mainTd.dataset.allProps);
        mainProps.__kaching_bundles = JSON.stringify({ ...props, bid: tier.bid });
        const mainItem = { id: +mainTd.dataset.variant, quantity: targetQty, properties: mainProps };
        /* selling plan follows the TIER: a bar with a pinned plan (per-tier
           subscription setups) switches the line to that plan; otherwise a
           subscribed line keeps its current plan (block-wide toggle setups,
           where the customer chose the delivery frequency). One-time lines
           stay one-time. */
        /* subscribed lines: a bar-pinned plan wins; else the delivery frequency
           follows the new quantity (1→shortest … 3+→longest); else keep the
           customer's plan. One-time buyers stay one-time. */
        const curPlan = mainTd.dataset.sellingPlan ? +mainTd.dataset.sellingPlan : null;
        let newPlan = null;
        if (curPlan) {
          const mapped = mapPlanForQty(cfg, +mainTd.dataset.variant, targetQty);
          newPlan = tier.spGid || (mapped ? mapped.id : curPlan);
        }
        if (newPlan) mainItem.selling_plan = newPlan;

        // Gift lines: preserve PAID extras beyond the old tier's granted qty.
        // Kaching itself splits a gift line into granted-free + paid-surplus,
        // so we re-add one line at granted+surplus and let it re-split.
        const currentGiftTotal = giftTds.reduce((n, td) => n + (+td.dataset.qty || 0), 0);
        const oldBar = cfg.bars.find(b => b.bid === props.bid);
        const oldGranted = oldBar ? oldBar.giftQty : currentGiftTotal;
        const surplus = Math.max(0, currentGiftTotal - oldGranted);

        // add.js prepends each item — push the GIFT first so the main
        // product ends up on top, matching Kaching's original line order
        const giftVariant = tier.giftVariantId || (giftTds[0] ? +giftTds[0].dataset.variant : null);
        const giftAllowed = !tier.giftOnlySubs || !!newPlan;   // subscription-only gifts need a subscribed main
        if (giftVariant && tier.giftId && tier.giftQty > 0 && giftAllowed) {
          const giftProps = giftTds[0] ? normProps(giftTds[0].dataset.allProps) : {};
          giftProps.__kaching_bundles = JSON.stringify({ id: group, deal: props.deal, gift: tier.giftId });
          items.push({ id: giftVariant, quantity: tier.giftQty + surplus, properties: giftProps });
        } else if (giftVariant && surplus > 0) {
          // tier grants no gift — keep the customer's paid wraps as a plain line
          items.push({ id: giftVariant, quantity: surplus });
        }
        items.push(mainItem);
      }

      await postJSON('/cart/update.js', { updates });
      mutated = true;
      if (items.length) {
        await postJSON('/cart/add.js', { items });
      }
      /* render AFTER the mutation settled: HTML embedded in the mutating
         request itself can predate Kaching's re-applied discounts (stale
         undiscounted prices/decimals in the drawer). A render failure only
         leaves stale DOM — never touch the cart over it. */
      await refreshSection(sectionId).catch(() => {});
    } catch (err) {
      // recovery: only re-add originals if the cart was actually zeroed —
      // restoring an untouched cart would duplicate the lines
      if (mutated) {
        try {
          const restore = originals.filter(o => o.quantity > 0).map(o => {
            const it = { id: o.id, quantity: o.quantity, properties: o.properties };
            if (o.sp) it.selling_plan = o.sp;
            return it;
          });
          if (restore.length) await postJSON('/cart/add.js', { items: restore.reverse() });
        } catch (e2) { /* cart state = whatever the server says; re-render below */ }
        await refreshSection(sectionId).catch(() => { location.reload(); });
      }
      console.warn('[vg-kb-sync]', err);
    } finally {
      inFlight.delete(group);
      const td = document.querySelector('td[data-vg-kb-main]');
      if (td) setBusy(td, group, false);
    }
  }

  /* re-render the section from server truth. NOTE: GET ?sections= does NOT
     work for header GROUP sections on product pages (returns the product
     section instead) — a no-op cart POST renders them correctly. */
  async function refreshSection(sectionId) {
    if (!sectionId) return;
    const j = await postJSON('/cart/update.js', { updates: {}, sections: sectionId });
    renderSection(sectionId, j.sections && j.sections[sectionId]);
  }

  /* ---------- delegated controls (our buttons carry vg-kb-* classes) ---------- */
  document.addEventListener('click', e => {
    const btn = e.target.closest('.vg-kb-minus, .vg-kb-plus, .vg-kb-remove');
    if (!btn) return;
    const td = btn.closest('td[data-vg-kb-main]');
    if (!td) return;
    e.preventDefault();
    e.stopPropagation();
    const cur = +td.dataset.qty || 1;
    if (btn.classList.contains('vg-kb-plus')) swap(td, cur + 1);
    else if (btn.classList.contains('vg-kb-minus')) swap(td, cur - 1);   // qty 1 → 0 removes the whole bundle
    else swap(td, 0);
  }, true);

  /* ---------- non-bundle drawer rows (vg-stk controls) ----------
     In the drawer, ALL rows render OUR buttons (see cart-products.liquid:
     Horizon's own handlers morph-replace the <dialog> — a visible
     close/reopen blink — and race concurrent updates). Changes go by
     line KEY (never index, so nothing shifts under us) and render via
     the same dialog-preserving transplant as bundle swaps. */
  const stockBusy = new Set();

  async function keyChange(key, qty, rowEl) {
    if (stockBusy.has(key)) return;
    stockBusy.add(key);
    if (rowEl) rowEl.classList.add('vg-kb-busy');
    const sectionId = sectionIdFor(rowEl || document.querySelector('.cart-drawer__dialog') || document.body);
    try {
      await postJSON('/cart/change.js', { id: key, quantity: qty });
      // settled render — see swap(); embedded-render prices can predate Kaching's discounts
      await refreshSection(sectionId).catch(() => {});
    } catch (err) {
      await refreshSection(sectionId).catch(() => {});
      console.warn('[vg-kb-sync]', err);
    } finally {
      stockBusy.delete(key);
      if (rowEl && rowEl.isConnected) rowEl.classList.remove('vg-kb-busy');
    }
  }

  document.addEventListener('click', e => {
    const btn = e.target.closest && e.target.closest('.vg-stk-minus, .vg-stk-plus, .vg-stk-remove');
    if (!btn) return;
    const td = btn.closest('td[data-vg-stk]');
    if (!td) return;
    e.preventDefault();
    e.stopPropagation();
    const cur = +td.dataset.qty || 0;
    const row = td.closest('tr');
    if (btn.classList.contains('vg-stk-plus')) keyChange(td.dataset.key, cur + 1, row);
    else if (btn.classList.contains('vg-stk-minus')) keyChange(td.dataset.key, Math.max(0, cur - 1), row);
    else keyChange(td.dataset.key, 0, row);
  }, true);

  /* ---------- subscription upsell (two-choice checkout stack) ----------
     Activates only when the cart holds a bundle main whose product has
     selling plans (runtime data — nothing configured). Shows the exact
     subscribed price; frequency follows quantity (1→shortest…3+→longest). */
  async function updateSubUpsell() {
    const box = document.querySelector('[data-vg-subup]');
    if (!box) return;
    if (box.dataset.state === 'sub') return;   // subscribed layout is fully server-rendered
    const ctas = box.closest('.cart__ctas');
    const serverActive = box.dataset.state === 'choice';
    const deactivate = () => {
      // the server rendered this state with correct prices — never blank it
      // because a runtime fetch failed; only the legacy JS-activated path hides
      if (serverActive) return;
      box.hidden = true;
      if (ctas) ctas.classList.remove('vg-subup-active');
    };
    const mains = [...document.querySelectorAll('td[data-vg-kb-main]')];
    if (!mains.length) return deactivate();
    if (serverActive) {
      /* prices + benefit texts are server-rendered from the cart's OWN live
         data (same render as the line prices — they can never disagree with
         the cart, even right after admin price changes). The product-page
         JSON this script reads CAN diverge from cart pricing, so it must
         never overwrite them. Only the frequency note is filled here. */
      try {
        const td = mains[0];
        const props = parseKb(td.dataset.props);
        if (props) {
          const cfg = await getDealConfig(td.dataset.url, props.bid);
          const plan = mapPlanForQty(cfg, +td.dataset.variant, +td.dataset.qty);
          const recEl = box.querySelector('[data-vg-sub-recurring]');
          if (recEl && plan) {
            recEl.textContent = plan.weeks > 0
              ? (recEl.dataset.tpl || '').replace('{weeks}', plan.weeks)
              : plan.name;
            recEl.hidden = false;
          }
        }
      } catch (e) { /* note stays hidden — prices unaffected */ }
      return;
    }
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()));
      let subTotal = cart.total_price;
      let onceTotal = cart.total_price;
      let plannable = 0;
      let firstPlan = null;
      for (const td of mains) {
        const props = parseKb(td.dataset.props);
        if (!props) continue;
        const cfg = await getDealConfig(td.dataset.url, props.bid);
        const vid = +td.dataset.variant;
        const qty = +td.dataset.qty;
        const subEst = estimateSubTotal(cfg, vid, qty);
        const onceEst = estimateOnceTotal(cfg, vid, qty);
        if (subEst == null || onceEst == null) continue;
        const line = cart.items.find(i => i.key === td.dataset.key);
        if (!line) continue;
        subTotal = subTotal - line.final_line_price + subEst;
        onceTotal = onceTotal - line.final_line_price + onceEst;
        if (!firstPlan) firstPlan = mapPlanForQty(cfg, vid, qty);
        plannable++;
      }
      if (!plannable) return deactivate();
      // frequency note above the subscribe button ("Recurring every X weeks")
      const recEl = box.querySelector('[data-vg-sub-recurring]');
      if (recEl && firstPlan) {
        recEl.textContent = firstPlan.weeks > 0
          ? (recEl.dataset.tpl || '').replace('{weeks}', firstPlan.weeks)
          : firstPlan.name;
        recEl.hidden = false;
      }
      // {discount} in the benefit lines → the mapped plan's live discount
      if (firstPlan && typeof firstPlan.pct === 'number') {
        box.querySelectorAll('[data-vg-benefit]').forEach(el => {
          const tpl = el.dataset.tpl || '';
          if (tpl.indexOf('{discount}') === -1) return;
          const txt = el.querySelector('.vg-subup__benefit-text');
          if (txt) txt.innerHTML = tpl.replace('{discount}', firstPlan.pct + '%');
        });
      }
      const subEl = box.querySelector('[data-vg-sub-price]');
      if (subEl) subEl.textContent = formatMoney(subTotal, cart.currency);
      const onceEl = box.querySelector('[data-vg-once-price]');
      if (onceEl) onceEl.textContent = formatMoney(onceTotal, cart.currency);
      box.hidden = false;
      if (ctas) ctas.classList.add('vg-subup-active');
    } catch (e) {
      deactivate();
    }
  }

  document.addEventListener('click', async e => {
    const sub = e.target.closest('.vg-sub-go');
    const once = e.target.closest('.vg-onetime-go');
    if (!sub && !once) return;
    e.preventDefault();
    e.stopPropagation();
    const box = e.target.closest('[data-vg-subup]');
    if (box) box.classList.add('vg-subup-busy');
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()));
      for (const line of cart.items) {
        const kb = line.properties && line.properties.__kaching_bundles;
        if (!kb || kb.indexOf('"main"') === -1) continue;
        if (sub) {
          const props = JSON.parse(kb);
          const cfg = await getDealConfig((line.url || '').split('?')[0], props.bid);
          const plan = mapPlanForQty(cfg, line.variant_id, line.quantity);
          const cur = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id;
          if (plan && cur !== plan.id) {
            await postJSON('/cart/change.js', { id: line.key, quantity: line.quantity, selling_plan: plan.id });
          }
        } else if (line.selling_plan_allocation) {
          await postJSON('/cart/change.js', { id: line.key, quantity: line.quantity, selling_plan: null });
        }
      }
      location.href = '/checkout';
    } catch (err) {
      console.warn('[vg-kb-sync]', err);
      if (box) box.classList.remove('vg-subup-busy');
    }
  }, true);

  /* ---------- PDP subscribe CTA (subscription-first) ----------
     The buy box defaults to subscription: the Kaching tier bars are
     rewritten to SUBSCRIPTION pricing (price + recalculated savings pill),
     the big CTA shows the sub total for the selected tier (struck =
     one-time total), and the text link above is a DIRECT one-time add.
     All totals come from this very page's Kaching JSON (no fetches).
     One-time link = the stock (hidden) ATC -> Kaching's untouched flow.
     Subscribe = same ATC, then the new main line is converted to the
     quantity-mapped plan; the auto-opened drawer re-renders to the banner. */
  const pdpBox = document.querySelector('[data-vg-pdp-cta]');
  if (pdpBox) (function initPdp(box) {
    let cfg = null;
    try { cfg = parseKachingConfigFromDoc(document, null); } catch (e) { cfg = null; }
    const stockAtc = document.querySelector('.rh-atc-wrap.vg-pdp-active .rh-atc');
    if (!cfg || !stockAtc) {
      // no deal block / no ATC — restore the stock button and step aside
      box.hidden = true;
      const wrap = document.querySelector('.rh-atc-wrap.vg-pdp-active');
      if (wrap) wrap.classList.remove('vg-pdp-active');
      return;
    }
    const variantId = +box.dataset.variant;
    const currency = cfg.currency || 'EUR';
    const linkEl = box.querySelector('[data-vg-pdp-mode-link]');
    const priceEl = box.querySelector('[data-vg-pdp-price]');
    const compareEl = box.querySelector('[data-vg-pdp-compare]');
    const recEl = box.querySelector('[data-vg-pdp-recurring]');

    const selectedQty = () => {
      const list = [...document.querySelectorAll('.kaching-bundles__bar')];
      const idx = list.findIndex(b => (b.className || '').indexOf('--selected') !== -1);
      if (idx === -1 || !cfg.barQtysDomOrder[idx]) return cfg.barQtysDomOrder[0] || 1;
      return cfg.barQtysDomOrder[idx];
    };

    /* one-time totals: deals with 0% breaks get their one-time tier discount
       from NATIVE Shopify automatic discounts (one-time-purchase-only) — not
       page-readable, so the percentages are mirrored in theme settings.
       Deals that still carry their own % breaks keep the Kaching math. */
    const onceDisc2 = parseFloat(box.dataset.onceDisc2) || 0;
    const onceDisc3 = parseFloat(box.dataset.onceDisc3) || 0;
    const pdpOnceTotal = (qty) => {
      const kach = estimateOnceTotal(cfg, variantId, qty);
      if (kach == null) return kach;
      const tier = pickTier(cfg.bars, qty);
      if (tier && tier.discType === 'percentage' && tier.discValue) return kach;
      const p = qty >= 3 ? onceDisc3 : qty >= 2 ? onceDisc2 : 0;
      if (!p) return kach;
      return Math.round(kach * (1 - p / 100));
    };

    /* rewrite every Kaching bar to subscription pricing: price -> sub total,
       savings pill -> compare-at minus sub total. Idempotent (originals kept
       in dataset), re-run on every update; bars display whole units only. */
    const syncBars = () => {
      const bars = [...document.querySelectorAll('.kaching-bundles__bar')];
      bars.forEach((bar, i) => {
        const qty = cfg.barQtysDomOrder[i];
        if (!qty) return;
        const sub = estimateSubTotal(cfg, variantId, qty);
        if (sub == null) return;
        const p = bar.querySelector('.kaching-bundles__bar-price');
        if (p) p.textContent = formatMoney(sub, currency);
        const full = bar.querySelector('.kaching-bundles__bar-full-price');
        const label = bar.querySelector('.kaching-bundles__bar-label');
        if (label && full) {
          if (!label.dataset.vgOrig) label.dataset.vgOrig = label.textContent;
          const cmp = parseInt((full.textContent.match(/\d+/g) || []).join(''), 10) * 100;
          if (cmp > sub) {
            label.textContent = label.dataset.vgOrig.replace(/\d[\d.,]*/, String(Math.round((cmp - sub) / 100)));
          }
        }
      });
    };

    const update = () => {
      const qty = selectedQty();
      const sub = estimateSubTotal(cfg, variantId, qty);
      const once = pdpOnceTotal(qty);
      if (sub == null || once == null) return;
      if (priceEl) priceEl.textContent = formatMoney(sub, currency);
      if (compareEl) {
        compareEl.textContent = formatMoney(once, currency);   // strike the one-time total
        compareEl.hidden = once <= sub;
      }
      if (linkEl) linkEl.textContent = (linkEl.dataset.onceTpl || '').replace('{price}', formatMoney(once, currency));
      const plan = mapPlanForQty(cfg, variantId, qty);
      if (recEl && plan) {
        recEl.textContent = plan.weeks > 0
          ? (recEl.dataset.tpl || '').replace('{weeks}', plan.weeks)
          : plan.name;
      }
      syncBars();
    };
    update();
    setTimeout(update, 800);   // failsafe after Kaching's own init/preselection
    document.addEventListener('click', e => {
      if (e.target.closest && e.target.closest('[class*="kaching-bundles"]')) setTimeout(update, 150);
    });
    /* subscription-first bars, no one-time flash: the hero ships a style
       keeping bar prices invisible until this section carries
       .vg-bars-synced. Sync the instant Kaching's bars mount (they render
       async — a late mount used to wait for the 800ms timer), then reveal;
       2.5s failsafe reveals no matter what. */
    const secEl = box.closest('[id^="shopify-section-"]');
    const reveal = () => { if (secEl) secEl.classList.add('vg-bars-synced'); };
    const armBars = () => {
      const firstBar = document.querySelector('.kaching-bundles__bar');
      if (!firstBar || !firstBar.parentElement) return false;
      update();
      reveal();
      // selection can also change programmatically — follow the bars' class flips
      new MutationObserver(() => update())
        .observe(firstBar.parentElement, { subtree: true, attributes: true, attributeFilter: ['class'] });
      return true;
    };
    if (!armBars() && secEl) {
      const mo = new MutationObserver(() => { if (armBars()) mo.disconnect(); });
      mo.observe(secEl, { childList: true, subtree: true });
    }
    setTimeout(reveal, 2500);

    /* ---- own single-request bundle add (v3 architecture) ----
       Kaching's client adds gift and main SEQUENTIALLY (~2.2s) and its
       discount function can't zero gifts under a subscribed main — both
       solved by adding the whole bundle ourselves in ONE items[] call:
       gift first, main last (add.js prepends, so the main renders on top),
       the selling plan directly on the main line for subscribe. Properties
       use Kaching's own live format so checkout + recurring keep matching:
         main {deal: nanoId, pp: productId, main:true, v: versionId, id, bid}
         gift {id, deal, v, gift: tierGiftId}
       All values come from this page's deal JSON at runtime (versionId
       rotates on every dashboard edit — never cache it hard).
       `?kaching_bundles=true` marks the request kaching-internal, so their
       interceptor skips it (their own exclusion list). Gift zeroing:
       native buy-X-get-Y automatic discounts (one-time AND subscribed);
       Kaching's function additionally zeroes the one-time case — same
       discount class, largest wins, still €0. */
    const nano4 = () => Math.random().toString(36).slice(2, 6);

    const buildItems = (subscribe) => {
      const qty = selectedQty();
      const tier = pickTier(cfg.bars, qty);
      const plan = subscribe ? mapPlanForQty(cfg, variantId, qty) : null;
      // ONE group id shared by main + gift — the discount function zeroes the
      // gift by this linkage (verified against their client's live payloads)
      const group = nano4();
      const items = [];
      if (tier && tier.giftVariantId && tier.giftQty > 0 && (!tier.giftOnlySubs || subscribe)) {
        items.push({
          id: tier.giftVariantId,
          quantity: tier.giftQty,
          properties: { __kaching_bundles: JSON.stringify({ id: group, deal: cfg.deal, v: cfg.dealV, gift: tier.giftId }) }
        });
      }
      const props = { deal: cfg.deal, pp: cfg.varProd && cfg.varProd[variantId], main: true, v: cfg.dealV, id: group, bid: tier ? tier.bid : null };
      const main = { id: variantId, quantity: qty, properties: { __kaching_bundles: JSON.stringify(props) } };
      if (plan) main.selling_plan = plan.id;
      items.push(main);
      return items;
    };

    const doAdd = async (subscribe) => {
      box.classList.add('vg-subup-busy');
      try {
        const dlg = document.querySelector('.cart-drawer__dialog');
        const secId = dlg ? sectionIdFor(dlg) : null;
        await postJSON('/cart/add.js?kaching_bundles=true', { items: buildItems(subscribe) });
        /* NEVER render sections from the mutating request — automatic
           discounts (2er/3er-Set, gift zeroing) may not be applied in that
           render yet (July settled-render gotcha; showed €98 instead of €89).
           A no-op update AFTER the add returns the settled state. */
        if (secId) await refreshSection(secId).catch(() => {});
        /* open once, complete — content is the settled render. ~1.2s after
           the click, transient user activation still valid. */
        const comp = dlg && dlg.closest('cart-drawer-component');
        if (comp && typeof comp.open === 'function') { try { comp.open(); } catch (e) { /* natural open */ } }
        // canary (warn only): every gift line must be €0 via Kaching's
        // function — if not, its property format drifted.
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()).then(c => {
          const paid = c.items.find(it => ((it.properties && it.properties.__kaching_bundles) || '').indexOf('"gift"') !== -1 && it.final_line_price > 0);
          if (paid) console.warn('[vg-kb-sync] CANARY: gift line not zeroed — check kaching property format');
        }).catch(() => {});
      } catch (err) {
        console.warn('[vg-kb-sync]', err);
      } finally {
        box.classList.remove('vg-subup-busy');
      }
    };

    // one-time link: same single add, no plan (native one-time tier discounts price it)
    if (linkEl) linkEl.addEventListener('click', e => {
      e.preventDefault();
      doAdd(false);
    });

    document.addEventListener('click', e => {
      const go = e.target.closest('[data-vg-pdp-go]');
      if (!go || !box.contains(go)) return;
      e.preventDefault();
      e.stopPropagation();
      doAdd(true);
    }, true);
  })(pdpBox);
})();
